/**
 * SupplierPaymentDetailModal — Detalle de un pago a proveedor
 * Muestra: datos del pago, estado, acciones (editar/confirmar/anular).
 */
import { useState } from 'react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { CheckCircle2, XCircle, Loader2, PencilLine } from 'lucide-react'
import { fmtDate, fmtDateTime } from '@/lib/utils'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import { DataTable } from '@/components/ds/DataTable'
import {
  useSupplierPayment, useConfirmSupplierPayment, useCancelSupplierPayment,
  useUpdateSupplierPayment, useSupplierPayments,
  type SupplierPayment,
} from '../hooks/usePurchases'
import { SupplierPaymentModal } from './SupplierPaymentModal'
import { displayLabelCls as labelCls } from '@/lib/formStyles'
import { usePermission } from '@/hooks/usePermission'
import { Modal } from '@/components/ds/Modal'

interface SupplierPaymentDetailModalProps {
  paymentId: string
  onClose: () => void
}

const PAY_STATUS: Record<string, { label: string; cls: string }> = {
  registered: { label: 'Registrado', cls: 'bg-warning-surface text-warning-content border-warning-border' },
  confirmed:  { label: 'Confirmado', cls: 'bg-success-surface text-success-content border-success-border' },
  cancelled:  { label: 'Anulado',    cls: 'bg-danger-surface text-danger-content border-danger-border' },
}

const fieldCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content min-h-[38px]'

/**
 * Historial de pagos de la factura asociada. Útil cuando hay pagos parciales:
 * muestra cuándo, cómo, quién registró y monto de cada pago.
 */
function PaymentHistory({ invoiceId, currentId }: { invoiceId: string; currentId: string }) {
  const locale = useTenantLocale()
  const { data } = useSupplierPayments({ invoice_id: invoiceId, page_size: 100 })
  const items = (data?.payments ?? [])
    .filter(p => p.status !== 'cancelled')
    .sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1))

  if (items.length <= 1) return null

  const fmtMoney = (v: string | undefined, cur?: string | null) => {
    if (!v) return '—'
    const n = parseFloat(v)
    const code = cur && cur !== 'CLP' ? cur : ''
    return `${code ? code + ' ' : '$'}${formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)}`
  }

  return (
    <div>
      <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">
        Historial de pagos de la factura ({items.length})
      </p>
      <DataTable
        columns={[
          { key: 'payment_date', header: 'Fecha', render: p => fmtDate(p.payment_date, locale) },
          { key: 'payment_method_display', header: 'Método' },
          { key: 'created_by_name', header: 'Registró', render: p => p.created_by_name || '—' },
          {
            key: 'amount', header: 'Monto', numeric: true,
            sortValue: p => Number(p.amount),
            render: p => (
              <>
                <span className="text-content">{fmtMoney(p.amount, p.currency)}</span>
                {p.currency && p.currency !== 'CLP' && p.amount_clp && (
                  <span className="block text-[11px] text-content-subtle">${formatNumber(parseFloat(p.amount_clp), locale, 0)}</span>
                )}
              </>
            ),
          },
        ]}
        rows={items}
        rowKey={p => p.id}
        // El pago que se está viendo se destaca la fila ENTERA.
        rowClassName={p => (p.id === currentId ? 'bg-primary-50/60 dark:bg-primary-900/20' : undefined)}
        // Ya vive dentro del modal.
        surface={false}
        minWidth={520}
        caption="Pagos del proveedor"
      />
    </div>
  )
}

export function SupplierPaymentDetailModal({ paymentId, onClose }: SupplierPaymentDetailModalProps) {
  const locale = useTenantLocale()
  const [editMode, setEditMode] = useState(false)

  const fmt = (value: string | undefined) => {
    if (!value) return '—'
    const n = parseFloat(value)
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }

  const { data: payment, isLoading } = useSupplierPayment(paymentId)
  const confirmPayment = useConfirmSupplierPayment()
  const cancelPayment = useCancelSupplierPayment()
  const updatePayment = useUpdateSupplierPayment(paymentId)
  const { confirm: confirmAction, modalProps } = useConfirmModal()
  const { can } = usePermission()
  const canEdit = can('purchases.payments', 'edit')
  const canEditConfirmed = can('purchases.payments', 'edit_confirmed')

  const handleSaveEdit = (formData: Record<string, unknown>) => {
    updatePayment.mutate(formData as Parameters<typeof updatePayment.mutate>[0], {
      onSuccess: () => {
        setEditMode(false)
        onClose()
      },
      onError: (e: any) => {
        const msg = e?.response?.data?.error?.message || 'No se pudo guardar la edición.'
        window.alert(msg)
      },
    })
  }

  const handleEditConfirmed = () => {
    confirmAction({
      message: 'Se va a EDITAR un pago CONFIRMADO. Esto actualizará su comprobante contable EN SITIO (sin reversa) y recalculará el estado de pago de la factura. Solo aplica en períodos abiertos. ¿Continuar?',
      variant: 'danger',
      confirmLabel: 'Editar de todas formas',
      onConfirm: () => setEditMode(true),
    })
  }

  const handleConfirm = () => {
    if (!payment) return
    confirmAction({
      message: `¿Confirmar el pago de ${fmt(payment.amount)} a ${payment.supplier_name}?`,
      variant: 'success',
      onConfirm: () => confirmPayment.mutate(payment.id),
    })
  }

  const handleCancel = () => {
    if (!payment) return
    confirmAction({
      message: `¿Anular el pago de ${fmt(payment.amount)} a ${payment.supplier_name}?`,
      variant: 'danger',
      onConfirm: () => cancelPayment.mutate(payment.id),
    })
  }

  const statusCfg = payment ? PAY_STATUS[payment.status] ?? PAY_STATUS.registered : PAY_STATUS.registered

  if (editMode && payment) {
    return (
      <SupplierPaymentModal
        payment={payment}
        onSave={handleSaveEdit}
        onClose={() => setEditMode(false)}
        isPending={updatePayment.isPending}
      />
    )
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title="Detalle de Pago a Proveedor"
        subtitle={payment && (
          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusCfg.cls}`}>
            {statusCfg.label}
          </span>
        )}
        actions={
          <>
            <div className="flex gap-2 mr-auto">
              {payment?.status === 'registered' && (
                <>
                  {canEdit && (
                    <button
                      onClick={() => setEditMode(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-content border border-divider rounded-lg hover:bg-soft transition-colors">
                      <PencilLine size={14} />
                      Editar
                    </button>
                  )}
                  <button
                    onClick={handleConfirm}
                    disabled={confirmPayment.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-success-fill text-white rounded-lg hover:bg-success-fill-hover transition-colors disabled:opacity-50">
                    {confirmPayment.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Confirmar Pago
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelPayment.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface transition-colors disabled:opacity-50">
                    {cancelPayment.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    Anular
                  </button>
                </>
              )}
              {payment?.status === 'confirmed' && (
                <>
                  {canEditConfirmed && (
                    <button
                      onClick={handleEditConfirmed}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors">
                      <PencilLine size={14} />
                      Editar (confirmado)
                    </button>
                  )}
                  <button
                    onClick={handleCancel}
                    disabled={cancelPayment.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface transition-colors disabled:opacity-50">
                    {cancelPayment.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    Anular Pago
                  </button>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
              Cerrar
            </button>
          </>
        }
      >
          <div className="space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-content-subtle">
                <Loader2 size={20} className="animate-spin mr-2" />Cargando...
              </div>
            ) : payment ? (
              <>
                {/* Datos del pago */}
                <div>
                  <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Información del Pago</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className={labelCls}>Proveedor</p>
                      <div className={fieldCls}>{payment.supplier_name}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Fecha de Pago</p>
                      <div className={fieldCls}>{fmtDate(payment.payment_date, locale)}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Monto</p>
                      <div className={`${fieldCls} font-semibold text-content`}>{fmt(payment.amount)}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Método de Pago</p>
                      <div className={fieldCls}>{payment.payment_method_display ?? payment.payment_method}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Referencia</p>
                      <div className={fieldCls}>{payment.reference || '—'}</div>
                    </div>
                    <div>
                      <p className={labelCls}>Estado</p>
                      <div className={fieldCls}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.cls}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className={labelCls}>Estado Contable</p>
                      <div className={fieldCls}>
                        {payment.accounting_status === 'accounted' ? (
                          <span className="inline-flex items-center gap-1 text-success-content-soft font-medium">
                            <CheckCircle2 size={14} /> Contabilizado
                          </span>
                        ) : (
                          <span className="text-content-subtle">Pendiente</span>
                        )}
                      </div>
                    </div>
                    {payment.notes && (
                      <div className="sm:col-span-2">
                        <p className={labelCls}>Notas</p>
                        <div className={fieldCls}>{payment.notes}</div>
                      </div>
                    )}
                    {payment.created_by_name && (
                      <div>
                        <p className={labelCls}>Registrado por</p>
                        <div className={fieldCls}>{payment.created_by_name}</div>
                      </div>
                    )}
                    <div>
                      <p className={labelCls}>Fecha de Registro</p>
                      <div className={fieldCls}>{fmtDateTime(payment.created_at, locale)}</div>
                    </div>
                  </div>
                </div>

                {payment.invoice && (
                  <PaymentHistory invoiceId={payment.invoice} currentId={payment.id} />
                )}
              </>
            ) : (
              <p className="text-center text-content-subtle py-8">Pago no encontrado.</p>
            )}
          </div>
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}
